import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { MemberSearch } from "@/components/MemberSearch";
import { MoneyInput, QuickAmounts } from "@/components/MoneyInput";
import { TxTable } from "@/components/TxTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ReceiptActions } from "@/components/Receipt";
import { fmtDateTime, FREQ_LABELS, rupiah, todayStr, TX_LABELS } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePoll } from "@/lib/live";
import { cn } from "@/lib/utils";

const Receipt = ({ tx, onClose }) => (
  <div className="panel border-primary/40 bg-primary/5 animate-rise" data-testid="tx-receipt">
    <div className="flex items-center gap-3">
      <CheckCircle2 className="h-8 w-8 text-primary" />
      <div><p className="font-heading font-bold">{TX_LABELS[tx.type]} berhasil</p><p className="text-xs text-muted-foreground">{fmtDateTime(tx.created_at)}</p></div>
    </div>
    <div className="mt-4 space-y-1.5 text-sm">
      <p className="flex justify-between"><span className="text-muted-foreground">Anggota</span><span className="font-medium">{tx.member_name}</span></p>
      <p className="flex justify-between"><span className="text-muted-foreground">Nominal</span><span className="font-mono font-bold" data-testid="receipt-amount">{rupiah(tx.amount)}</span></p>
      {tx.balance_after !== undefined && <p className="flex justify-between"><span className="text-muted-foreground">Saldo simpanan</span><span className="font-mono" data-testid="receipt-balance">{rupiah(tx.balance_after)}</span></p>}
      {tx.remaining_after !== undefined && <p className="flex justify-between"><span className="text-muted-foreground">Angsuran ke / sisa</span><span className="font-mono" data-testid="receipt-remaining">{tx.installment_no} · {rupiah(tx.remaining_after)}</span></p>}
    </div>
    <div className="mt-5"><ReceiptActions tx={tx} testId="receipt" /></div>
    <Button variant="outline" className="w-full mt-2 h-11 rounded-full" onClick={onClose} data-testid="receipt-new-tx">Transaksi Baru</Button>
  </div>
);

const TAB_LABELS = { setor: "Setor", tarik: "Tarik", angsuran: "Angsuran" };

const LoanPicker = ({ loans, value, onChange }) => {
  if (!loans.length) return <p className="text-sm text-muted-foreground rounded-xl border border-dashed p-4" data-testid="no-running-loan">Anggota tidak memiliki pinjaman berjalan.</p>;
  return (
    <div className="space-y-2">
      {loans.map((l) => (
        <button key={l.id} type="button" onClick={() => onChange(l)} data-testid={`pick-loan-${l.id}`}
          className={cn("w-full text-left rounded-2xl border-2 p-4 transition-colors", value?.id === l.id ? "border-primary bg-primary/5" : "hover:bg-muted")}>
          <div className="flex justify-between"><span className="font-mono font-semibold">{rupiah(l.installment_amount)}</span><span className="text-xs font-semibold">{FREQ_LABELS[l.frequency]}</span></div>
          <p className="text-xs text-muted-foreground mt-1">{l.loan_no ? `${l.loan_no} · ` : ""}Pokok {rupiah(l.principal)} · ke-{l.paid_count + 1} dari {l.installment_count} · sisa {rupiah(l.total_payable - l.paid_amount)}{l.resort_name ? ` · Resort ${l.resort_name}` : ""}</p>
        </button>
      ))}
    </div>
  );
};

export default function TransaksiPage({ modes = ["setor", "tarik", "angsuran"], title = "Transaksi Cepat" }) {
  const { user } = useAuth();
  const canWithdraw = ["kasir", "admin_cabang"].includes(user.role);
  const visible = modes.filter((m) => m !== "tarik" || canWithdraw);
  const [mode, setMode] = useState(visible[0]);
  const [member, setMember] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loans, setLoans] = useState([]);
  const [loan, setLoan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [mine, setMine] = useState([]);
  const location = useLocation();
  useEffect(() => {
    const nik = location.state?.nik;
    if (nik) api.get(`/members/${nik}`).then((r) => setMember(r.data)).catch(() => {});
  }, [location.state]);

  const isAngsuran = visible.length === 1 && visible[0] === "angsuran";
  const [range, setRange] = useState(null);
  const loadMine = () => {
    if (!range) return;
    api.get("/transactions", { params: isAngsuran ? { type: "angsuran", ...range, limit: 2000 } : { ...range, limit: 2000 } })
      .then((r) => setMine(r.data.filter((t) => (isAngsuran ? ["angsuran"] : ["setor", "tarik", "potongan"]).includes(t.type)))).catch(() => {});
  };
  useEffect(() => { loadMine(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(loadMine);
  useEffect(() => {
    setLoan(null);
    if (!member) { setLoans([]); return; }
    api.get("/loans", { params: { nik: member.nik, status: "berjalan" } }).then((r) => {
      setLoans(r.data);
      const pre = r.data.find((l) => l.id === location.state?.loan_id);
      if (pre || r.data.length === 1) setLoan(pre || r.data[0]);
    }).catch(() => setLoans([]));
  }, [member]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mode === "angsuran" && loan) setAmount(String(Math.min(loan.installment_amount, loan.total_payable - loan.paid_amount))); }, [loan, mode]);

  const reset = () => { setReceipt(null); setMember(null); setAmount(""); setNote(""); };
  const [confirm, setConfirm] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!member || !Number(amount)) { toast.error("Pilih anggota dan isi nominal"); return; }
    if (mode === "angsuran") setConfirm(true); else doSubmit();
  };
  const doSubmit = async () => {
    setBusy(true);
    try {
      const { data } = mode === "angsuran"
        ? await api.post("/installments", { loan_id: loan.id, amount: Number(amount), note })
        : await api.post("/savings", { nik: member.nik, type: mode, amount: Number(amount), note });
      setReceipt(data);
      toast.success(`${TX_LABELS[data.type]} ${rupiah(data.amount)} tercatat`);
      loadMine();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl">
      <PageHeader title={title} />
      <div className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-6 [&>*]:min-w-0">
        {receipt ? <Receipt tx={receipt} onClose={reset} /> : (
          <form onSubmit={submit} className="panel space-y-5" data-testid="quick-tx-form">
            {visible.length > 1 && (
              <Tabs value={mode} onValueChange={(v) => { setMode(v); setAmount(""); }}>
                <TabsList className={cn("grid w-full h-12", visible.length === 3 ? "grid-cols-3" : "grid-cols-2")}>
                  {visible.map((m) => <TabsTrigger key={m} value={m} className="h-10" data-testid={`tab-${m}`}>{TAB_LABELS[m]}</TabsTrigger>)}
                </TabsList>
              </Tabs>
            )}
            <MemberSearch value={member} onSelect={setMember} />
            {member && mode === "angsuran" && <LoanPicker loans={loans} value={loan} onChange={setLoan} />}
            {member && (mode !== "angsuran" || loan) && (
              <>
                <div className="space-y-3">
                  <label className="text-sm font-medium">Nominal</label>
                  <MoneyInput value={amount} onChange={setAmount} className="h-16 text-2xl font-semibold rounded-2xl" testId="quick-amount-input" />
                  {mode !== "angsuran" && <QuickAmounts onPick={setAmount} />}
                </div>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" className="h-11" data-testid="quick-note-input" />
                <Button type="submit" disabled={busy} className="w-full h-14 rounded-2xl text-base font-semibold" data-testid="quick-tx-submit-btn">
                  {busy ? "Menyimpan..." : `Simpan ${TX_LABELS[mode]}${amount ? ` · ${rupiah(amount)}` : ""}`}
                </Button>
              </>
            )}
            {user.role === "petugas" && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Transaksi lapangan atas nama Anda.</p>}
          </form>
        )}
        {isAngsuran ? (
          <div className="space-y-4" data-testid="installment-history">
            <PeriodFilter onChange={setRange} testId="installment-period" />
            <div className="rounded-3xl bg-primary text-primary-foreground p-5 shadow-md" data-testid="installment-total-card">
              <p className="text-xs uppercase tracking-wider opacity-80">Total Angsuran</p>
              <p className="font-heading font-mono text-2xl font-bold mt-1" data-testid="installment-total">{rupiah(mine.reduce((s, t) => s + t.amount, 0))}</p>
              <p className="text-xs opacity-80 mt-1">{mine.length} transaksi</p>
            </div>
            <div className="panel">
            <p className="font-heading font-semibold mb-3">Riwayat Angsuran</p>
            <TxTable rows={mine} testId="installment-history-table" />
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="savings-history">
            <PeriodFilter onChange={setRange} testId="savings-period" />
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-3xl bg-primary text-primary-foreground p-4 sm:p-5 shadow-md" data-testid="savings-in-card">
                <p className="text-[11px] uppercase tracking-wider opacity-80">Simpanan</p>
                <p className="font-heading text-lg sm:text-2xl font-bold mt-1 tabular-nums break-words" data-testid="savings-in-total">{rupiah(mine.filter((t) => t.type !== "tarik").reduce((s, t) => s + t.amount, 0))}</p>
                <p className="text-[11px] opacity-80 mt-1">{mine.filter((t) => t.type !== "tarik").length} transaksi</p>
              </div>
              <div className="rounded-3xl bg-rose-600 text-white p-4 sm:p-5 shadow-md" data-testid="savings-out-card">
                <p className="text-[11px] uppercase tracking-wider opacity-80">Penarikan</p>
                <p className="font-heading text-lg sm:text-2xl font-bold mt-1 tabular-nums break-words" data-testid="savings-out-total">{rupiah(mine.filter((t) => t.type === "tarik").reduce((s, t) => s + t.amount, 0))}</p>
                <p className="text-[11px] opacity-80 mt-1">{mine.filter((t) => t.type === "tarik").length} transaksi</p>
              </div>
            </div>
            <div className="panel">
              <p className="font-heading font-semibold mb-3">Riwayat Simpanan</p>
              <TxTable rows={mine} testId="my-tx" />
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Konfirmasi Pembayaran Angsuran" onConfirm={doSubmit}
        description={member && loan && <>Bayar angsuran <b>{rupiah(amount)}</b> untuk <b>{member.name}</b>{loan.loan_no ? ` (${loan.loan_no})` : ""}?</>} confirmLabel="Ya, bayar" />
    </div>
  );
}
